import React, { useState, useEffect, useCallback, lazy, Suspense } from "react";
import adminService from "../services/adminService";
import { useRole } from "../contexts/RoleContext";
import { useNavigate } from "react-router-dom";

const AdminMeetings = lazy(() => import("./AdminMeetings"));
const AdminReports = lazy(() => import("./AdminReports"));
const AdminPrompts = lazy(() => import("./AdminPrompts"));
const AdminSettings = lazy(() => import("./AdminSettings"));

const TabFallback = () => (
  <div className="flex justify-center items-center py-16">
    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#e04665]"></div>
  </div>
);

const AdminPanel = () => {
  const { role, permissions, loading: roleLoading } = useRole();
  const navigate = useNavigate();

  // Tab state
  const [activeTab, setActiveTab] = useState("users"); // 'users', 'meetings', 'reports', 'prompts'

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [limit] = useState(20);

  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchDebounce, setSearchDebounce] = useState("");

  // Add Admin Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]); // Changed to array for multi-select
  const [selectedPrivilege, setSelectedPrivilege] = useState("admin");
  const [addReason, setAddReason] = useState("");
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Revoke Modal state
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [revokeReason, setRevokeReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Authorization check
  useEffect(() => {
    if (!roleLoading && role !== "global_admin") {
      navigate("/meetings");
    }
  }, [role, roleLoading, navigate]);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounce(searchQuery);
      setCurrentPage(1); // Reset to first page on search
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch users
  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await adminService.listUsers({
        page: currentPage,
        limit,
        search: searchDebounce,
      });

      // Response structure: { status, users, pagination }
      setUsers(response.users || []);
      setTotalPages(response.pagination?.total_pages || 1);
      setTotalUsers(response.pagination?.total_users || 0);
    } catch (err) {
      console.error("Error fetching users:", err);
      setError(err.message || "Failed to fetch users");
    } finally {
      setLoading(false);
    }
  }, [currentPage, limit, searchDebounce]);

  useEffect(() => {
    if (!roleLoading && role === "global_admin") {
      fetchUsers();
    }
  }, [fetchUsers, role, roleLoading]);

  // Fetch all users for Add Admin dropdown
  const fetchAllUsers = useCallback(async (search = "") => {
    try {
      setLoadingUsers(true);
      const users = await adminService.listAllOrgUsers({ search });
      setAllUsers(users);
    } catch (err) {
      console.error("Error fetching all users:", err);
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  // Open Add Admin modal
  const handleAddAdminClick = () => {
    setShowAddModal(true);
    setUserSearch("");
    setSelectedUsers([]); // Reset to empty array
    setSelectedPrivilege("admin");
    setAddReason("");
    fetchAllUsers("");
  };

  // Handle user search in dropdown
  useEffect(() => {
    if (showAddModal) {
      setLoadingUsers(true); // Show loading while debouncing
      const timer = setTimeout(() => {
        fetchAllUsers(userSearch);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [userSearch, showAddModal, fetchAllUsers]);

  // Handle Add Admin submit
  const handleAddAdminSubmit = async () => {
    if (selectedUsers.length === 0) {
      alert("Please select at least one user");
      return;
    }
    // Reason is optional

    try {
      setSubmitting(true);
      setError(null);

      // Grant privileges to all selected users
      const userIds = selectedUsers.map((u) => u.user_id);
      await adminService.grantPrivilege({
        user_ids: userIds,
        privilege: selectedPrivilege,
        reason: addReason.trim(),
      });

      const userCount = selectedUsers.length;
      const userNames =
        selectedUsers.length <= 3
          ? selectedUsers.map((u) => u.name).join(", ")
          : `${selectedUsers.length} users`;

      setSuccess(`Successfully granted ${selectedPrivilege} to ${userNames}`);

      // Close modal immediately
      setShowAddModal(false);
      setSelectedUsers([]);
      setSubmitting(false);

      // Refresh admin list in background
      fetchUsers();

      // Clear success message after 5 seconds
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      console.error("Error granting privilege:", err);
      setError(err.message || "Failed to grant privilege");
      setSubmitting(false);
    }
  };

  // Handle revoke action
  const handleRevokeClick = (user) => {
    setSelectedUser(user);
    setRevokeReason("");
    setShowRevokeModal(true);
  };

  // Handle revoke submit
  const handleRevokeSubmit = async () => {
    // Reason is optional

    try {
      setSubmitting(true);
      setError(null);

      const privilege = selectedUser.is_global_admin ? "global_admin" : "admin";

      await adminService.revokePrivilege({
        user_id: selectedUser.user_id,
        privilege,
        reason: revokeReason.trim() || "",
      });

      setSuccess(
        `Successfully revoked ${privilege} from ${
          selectedUser.name || selectedUser.user_id
        }`,
      );

      // Close modal immediately
      setShowRevokeModal(false);
      setSelectedUser(null);
      setSubmitting(false);

      // Refresh admin list in background
      fetchUsers();

      // Clear success message after 5 seconds
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      console.error("Error revoking privilege:", err);
      setError(err.message || "Failed to revoke privilege");
      setSubmitting(false);
    }
  };

  // Handle action button clicks (legacy - can be removed)
  const handleActionClick = (action, user) => {
    handleRevokeClick(user);
  };

  // Handle modal submit (legacy - can be removed)
  const handleModalSubmit = async () => {
    await handleRevokeSubmit();
  };

  // Get role badge
  const getRoleBadge = (user) => {
    if (user.is_global_admin) {
      return (
        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800">
          🌐 Global Admin
        </span>
      );
    } else if (user.is_admin) {
      return (
        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
          👑 Admin
        </span>
      );
    } else {
      return (
        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-800">
          👤 Normal User
        </span>
      );
    }
  };

  // Get action label
  const getActionLabel = () => {
    switch (modalAction) {
      case "grant_admin":
        return "Grant Admin Privilege";
      case "grant_global":
        return "Grant Global Admin Privilege";
      case "revoke_admin":
        return "Revoke Admin Privilege";
      case "revoke_global":
        return "Revoke Global Admin Privilege";
      default:
        return "Confirm Action";
    }
  };

  if (roleLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (role !== "global_admin") {
    return null; // Will redirect
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Admin Panel</h1>
          <p className="mt-2 text-gray-600">
            Manage users, view meetings, and analyze performance
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="mb-6 border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab("users")}
              className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === "users"
                  ? "border-[#e04665] text-[#e04665]"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <span className="flex items-center gap-2">
                <svg
                  className="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                  />
                </svg>
                User Management
              </span>
            </button>

            <button
              onClick={() => setActiveTab("meetings")}
              className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === "meetings"
                  ? "border-[#e04665] text-[#e04665]"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <span className="flex items-center gap-2">
                <svg
                  className="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                All Meetings
              </span>
            </button>

            <button
              onClick={() => setActiveTab("reports")}
              className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === "reports"
                  ? "border-[#e04665] text-[#e04665]"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <span className="flex items-center gap-2">
                <svg
                  className="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
                Salesperson Reports
              </span>
            </button>

            <button
              onClick={() => setActiveTab("prompts")}
              className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === "prompts"
                  ? "border-[#e04665] text-[#e04665]"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <span className="flex items-center gap-2">
                <svg
                  className="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
                System Prompts
              </span>
            </button>

            <button
              onClick={() => setActiveTab("settings")}
              className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === "settings"
                  ? "border-[#e04665] text-[#e04665]"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <span className="flex items-center gap-2">
                <svg
                  className="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                Runtime Settings
              </span>
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === "users" && (
          <div>
            {/* User Management Header with Add Admin Button */}
            <div className="mb-6 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  User Management
                </h2>
                <p className="mt-1 text-gray-600">
                  Grant and revoke admin privileges
                </p>
              </div>
              <button
                onClick={handleAddAdminClick}
                className="px-6 py-3 bg-[#e04665] text-white font-semibold rounded-lg hover:bg-[#d9475a] transition-colors flex items-center gap-2"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                    clipRule="evenodd"
                  />
                </svg>
                Add Admin
              </button>
            </div>

            {/* Success Alert */}
            {success && (
              <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center">
                  <svg
                    className="h-5 w-5 text-green-400 mr-2"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <p className="text-green-800">{success}</p>
                </div>
              </div>
            )}

            {/* Error Alert */}
            {error && (
              <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center">
                  <svg
                    className="h-5 w-5 text-red-400 mr-2"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <p className="text-red-800">{error}</p>
                </div>
              </div>
            )}

            {/* Search Bar */}
            <div className="mb-6">
              <input
                type="text"
                placeholder="Search users by ID or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Users Table */}
            <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
              {loading ? (
                <div className="flex justify-center items-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
              ) : users.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  {searchDebounce
                    ? "No users found matching your search"
                    : "No users found"}
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Name
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Email
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Role
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {users.map((user) => (
                          <tr key={user.user_id} className="hover:bg-gray-50">
                            {/* <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">
                            {user.user_id}
                          </div>
                        </td> */}
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">
                                {user.name || "Unknown User"}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-500">
                                {user.email}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {getRoleBadge(user)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              <button
                                onClick={() => handleRevokeClick(user)}
                                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors font-semibold"
                              >
                                Revoke
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t border-gray-200">
                    <div className="text-sm text-gray-700">
                      Showing{" "}
                      <span className="font-medium">
                        {(currentPage - 1) * limit + 1}
                      </span>{" "}
                      to{" "}
                      <span className="font-medium">
                        {Math.min(currentPage * limit, totalUsers)}
                      </span>{" "}
                      of <span className="font-medium">{totalUsers}</span> users
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() =>
                          setCurrentPage((p) => Math.max(1, p - 1))
                        }
                        disabled={currentPage === 1}
                        className={`px-4 py-2 rounded-lg ${
                          currentPage === 1
                            ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                            : "bg-blue-600 text-white hover:bg-blue-700"
                        }`}
                      >
                        Previous
                      </button>
                      <span className="px-4 py-2 text-gray-700">
                        Page {currentPage} of {totalPages}
                      </span>
                      <button
                        onClick={() =>
                          setCurrentPage((p) => Math.min(totalPages, p + 1))
                        }
                        disabled={currentPage === totalPages}
                        className={`px-4 py-2 rounded-lg ${
                          currentPage === totalPages
                            ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                            : "bg-blue-600 text-white hover:bg-blue-700"
                        }`}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Close User Management Container */}
          </div>
        )}

        {/* Meetings Tab */}
        {activeTab === "meetings" && (
          <Suspense fallback={<TabFallback />}>
            <AdminMeetings />
          </Suspense>
        )}

        {/* Reports Tab */}
        {activeTab === "reports" && (
          <Suspense fallback={<TabFallback />}>
            <AdminReports />
          </Suspense>
        )}

        {/* System Prompts Tab */}
        {activeTab === "prompts" && (
          <Suspense fallback={<TabFallback />}>
            <AdminPrompts />
          </Suspense>
        )}

        {/* Runtime Settings Tab */}
        {activeTab === "settings" && (
          <Suspense fallback={<TabFallback />}>
            <AdminSettings />
          </Suspense>
        )}

        {/* Modals (Common for all tabs) */}
        {/* Add Admin Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  Add Admin User
                </h3>
              </div>
              <div className="px-6 py-4">
                {/* User Selection Dropdown */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Users:{" "}
                    {selectedUsers.length > 0 && (
                      <span className="text-[#e04665]">
                        ({selectedUsers.length} selected)
                      </span>
                    )}
                  </label>
                  <input
                    type="text"
                    placeholder="Search users by name or email..."
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#e04665] focus:border-[#e04665] mb-2"
                  />
                  {loadingUsers ? (
                    <div className="text-center py-4 text-gray-500">
                      Loading users...
                    </div>
                  ) : (
                    <div className="max-h-60 overflow-y-auto border border-gray-300 rounded-lg">
                      {allUsers.length === 0 ? (
                        <div className="text-center py-4 text-gray-500">
                          No users found
                        </div>
                      ) : (
                        <>
                          {/* Select All Option */}
                          <div className="px-4 py-2 bg-gray-50 border-b border-gray-300 sticky top-0">
                            <label className="flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={
                                  selectedUsers.length === allUsers.length &&
                                  allUsers.length > 0
                                }
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedUsers([...allUsers]);
                                  } else {
                                    setSelectedUsers([]);
                                  }
                                }}
                                className="mr-3 h-4 w-4 text-[#e04665] focus:ring-[#e04665] border-gray-300 rounded"
                              />
                              <span className="font-medium text-sm text-gray-700">
                                Select All ({allUsers.length} users)
                              </span>
                            </label>
                          </div>
                          {allUsers.map((user) => {
                            const isSelected = selectedUsers.some(
                              (u) => u.user_id === user.user_id,
                            );
                            return (
                              <label
                                key={user.user_id}
                                className={`flex items-start px-4 py-3 cursor-pointer hover:bg-[#e04665]/10 border-b border-gray-100 ${
                                  isSelected ? "bg-[#e04665]/20" : ""
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedUsers([
                                        ...selectedUsers,
                                        user,
                                      ]);
                                    } else {
                                      setSelectedUsers(
                                        selectedUsers.filter(
                                          (u) => u.user_id !== user.user_id,
                                        ),
                                      );
                                    }
                                  }}
                                  className="mr-3 mt-1 h-4 w-4 text-[#e04665] focus:ring-[#e04665] border-gray-300 rounded"
                                />
                                <div className="flex-1">
                                  <div className="font-medium text-gray-900">
                                    {user.name}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {user.email}
                                  </div>
                                  {user.job_title && (
                                    <div className="text-xs text-gray-400 mt-1">
                                      {user.job_title}
                                    </div>
                                  )}
                                </div>
                              </label>
                            );
                          })}
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* Privilege Selection */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Privilege:
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center p-3 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                      <input
                        type="radio"
                        name="privilege"
                        value="admin"
                        checked={selectedPrivilege === "admin"}
                        onChange={(e) => setSelectedPrivilege(e.target.value)}
                        className="mr-3"
                      />
                      <div>
                        <div className="font-medium text-gray-900">
                          👑 Admin
                        </div>
                        <div className="text-xs text-gray-500">
                          Can view analytics for their team
                        </div>
                      </div>
                    </label>
                    <label className="flex items-center p-3 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                      <input
                        type="radio"
                        name="privilege"
                        value="global_admin"
                        checked={selectedPrivilege === "global_admin"}
                        onChange={(e) => setSelectedPrivilege(e.target.value)}
                        className="mr-3"
                      />
                      <div>
                        <div className="font-medium text-gray-900">
                          🌐 Global Admin
                        </div>
                        <div className="text-xs text-gray-500">
                          Full access to all features and admin panel
                        </div>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Reason */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reason (optional):
                  </label>
                  <textarea
                    value={addReason}
                    onChange={(e) => setAddReason(e.target.value)}
                    placeholder="Provide a reason for granting this privilege..."
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setSelectedUsers([]);
                    setUserSearch("");
                    setAddReason("");
                  }}
                  disabled={submitting}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddAdminSubmit}
                  disabled={submitting || selectedUsers.length === 0}
                  className="px-4 py-2 bg-[#e04665] text-white rounded-lg hover:bg-[#d9475a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting
                    ? "Adding..."
                    : `Add Admin${selectedUsers.length > 1 ? "s" : ""} (${
                        selectedUsers.length
                      })`}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Revoke Modal */}
        {showRevokeModal && selectedUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  Revoke Admin Privileges
                </h3>
              </div>
              <div className="px-6 py-4">
                <p className="text-sm text-gray-600 mb-4">
                  You are about to revoke admin privileges from:
                </p>
                <div className="bg-gray-50 rounded-lg p-3 mb-4">
                  <p className="text-sm font-medium text-gray-900">
                    {selectedUser.name || "Unknown User"}
                  </p>
                  <p className="text-sm text-gray-600">{selectedUser.email}</p>
                  <p className="text-sm text-gray-600 mt-1">
                    Current Role: {getRoleBadge(selectedUser)}
                  </p>
                </div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason (optional):
                </label>
                <textarea
                  value={revokeReason}
                  onChange={(e) => setRevokeReason(e.target.value)}
                  placeholder="Provide a reason for revoking privileges..."
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowRevokeModal(false);
                    setSelectedUser(null);
                    setRevokeReason("");
                  }}
                  disabled={submitting}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRevokeSubmit}
                  disabled={submitting}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? "Revoking..." : "Revoke"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Close entire admin panel container */}
      </div>
    </div>
  );
};

export default AdminPanel;
